<template>
  <div v-if="visible" class="position-preview-modal" @click.self="close">
    <section class="position-preview-modal__panel" role="dialog" aria-modal="true" :aria-labelledby="titleId">
      <header class="position-preview-modal__header">
        <div>
          <h2 :id="titleId">{{ title }}</h2>
          <p>{{ exchange }}</p>
        </div>
        <button type="button" :aria-label="$tk('robot.position_preview_close')" @click="close">
          <i class="el-icon-close" />
        </button>
      </header>

      <p v-if="category === 'term'" class="position-preview-modal__tip">
        <i class="el-icon-info" />
        {{ $tk('robot.recommended') }}
      </p>

      <div v-if="primaryTabs.length > 1" class="position-preview-tabs" role="tablist">
        <button
          v-for="tab in primaryTabs"
          :key="tab.value"
          :class="{ active: primaryValue === tab.value }"
          type="button"
          role="tab"
          :aria-selected="primaryValue === tab.value ? 'true' : 'false'"
          @click="selectPrimary(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="showSettlementTabs" class="position-preview-tabs position-preview-tabs--secondary" role="tablist">
        <button
          v-for="tab in settlementTabs"
          :key="tab.value"
          :class="{ active: futureType === tab.value }"
          type="button"
          role="tab"
          :aria-selected="futureType === tab.value ? 'true' : 'false'"
          @click="selectSettlement(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="position-preview-filter">
        <i class="el-icon-search" aria-hidden="true" />
        <input
          v-model.trim="coinKeyword"
          type="text"
          :placeholder="$tk('robot.position_preview_filter_placeholder')"
        />
        <button
          v-if="coinKeyword"
          type="button"
          :aria-label="$tk('robot.position_preview_clear_filter')"
          @click="coinKeyword = ''"
        >
          <i class="el-icon-circle-close" aria-hidden="true" />
        </button>
      </div>

      <div class="position-preview-table-wrap">
        <table class="position-preview-table">
          <thead>
            <tr>
              <th>{{ $tk('robot.transaction_currency') }}</th>
              <th>
                <button type="button" class="position-preview-table__sort" @click="toggleSort">
                  {{ exchange }} {{ $tk('robot.minimum_position') }}
                  <i :class="coinSort === 1 ? 'el-icon-top' : 'el-icon-bottom'" />
                  <span class="sr-only">
                    {{ coinSort === 1 ? $tk('robot.position_preview_sort_ascending') : $tk('robot.position_preview_sort_descending') }}
                  </span>
                </button>
              </th>
              <th>{{ $tk('robot.recommended_positions') }}</th>
            </tr>
          </thead>
          <tbody v-if="!loading && !error && filteredRows.length">
            <tr v-for="(row, index) in filteredRows" :key="`${row.coin}-${index}`">
              <td>{{ row.coin }}</td>
              <td>{{ displayAmount(row.minSpace) }}</td>
              <td>{{ displayAmount(row.suggestedSpace) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="loading" class="position-preview-state">
          <i class="el-icon-loading" />
          <span>{{ $tk('robot.position_preview_loading') }}</span>
        </div>
        <div v-else-if="error" class="position-preview-state position-preview-state--error">
          <i class="el-icon-warning-outline" />
          <span>{{ $tk('robot.position_preview_failed') }}</span>
          <button type="button" @click="load">{{ $tk('robot.position_preview_retry') }}</button>
        </div>
        <div v-else-if="!filteredRows.length" class="position-preview-state">
          <i class="el-icon-document" />
          <span>{{ $tk('robot.no_data') }}</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { loadStrategyPositionPreview } from '@/api/strategy'

export default {
  name: 'StrategyPositionPreviewModal',
  props: {
    visible: { type: Boolean, default: false },
    category: { type: String, default: 'term' },
    robotType: { type: Number, default: null },
    exchange: { type: String, default: '' }
  },
  data() {
    return {
      primaryValue: 0,
      futureType: 1,
      coinSort: 2,
      coinKeyword: '',
      rows: [],
      loading: false,
      error: false,
      requestId: 0
    }
  },
  computed: {
    titleId() {
      return `position-preview-title-${this._uid}`
    },
    title() {
      const prefix = this.category === 'term'
        ? this.$tk('robot.futures_spot_simple')
        : this.category === 'hedge'
          ? this.$tk('robot.hedge')
          : 'UPlus-X'
      return `${prefix} ${this.$tk('robot.strategy_position')}`
    },
    primaryTabs() {
      if (this.category === 'term') {
        return [
          { value: 0, label: this.$tk('robot.regular_version') },
          { value: 1, label: this.$tk('robot.rapidly_version') },
          { value: 2, label: this.$tk('robot.BTC_version') }
        ]
      }
      if (this.category === 'vip') {
        return [
          { value: 0, label: this.$tk('robot.regular_version') },
          { value: 1, label: 'Pro' }
        ]
      }
      if ([2, 3].includes(Number(this.robotType))) {
        return [{ value: 1, label: this.$tk('robot.single_version') }]
      }
      return [
        { value: 0, label: this.$tk('robot.regular_version') },
        { value: 1, label: this.$tk('robot.single_version') }
      ]
    },
    settlementTabs() {
      return [
        { value: 1, label: this.$tk('robot.u_standard') },
        { value: 2, label: this.$tk('robot.currency_standard') }
      ]
    },
    showSettlementTabs() {
      return this.category === 'hedge' && this.primaryValue === 1 && ![2, 3].includes(Number(this.robotType))
    },
    amountUnit() {
      return this.category === 'hedge' && this.primaryValue === 1 && this.futureType === 2
        ? ''
        : 'USDT'
    },
    filteredRows() {
      const keyword = String(this.coinKeyword || '').trim().toUpperCase()
      if (!keyword) return this.rows
      return this.rows.filter(row => String((row && row.coin) || '').toUpperCase().includes(keyword))
    }
  },
  watch: {
    visible(value) {
      this.setPreviewLoadingLayer(value)
      if (value) {
        this.reset()
        this.$nextTick(() => this.load())
      } else {
        this.requestId += 1
      }
    },
    robotType(value, oldValue) {
      if (!this.visible || value === oldValue) return
      if (value === null || value === undefined || !Number.isFinite(Number(value))) return
      this.reset()
      this.$nextTick(() => this.load())
    },
    category(value, oldValue) {
      if (!this.visible || value === oldValue) return
      this.reset()
      this.$nextTick(() => this.load())
    }
  },
  mounted() {
    this.setPreviewLoadingLayer(this.visible)
  },
  beforeDestroy() {
    this.setPreviewLoadingLayer(false)
  },
  methods: {
    setPreviewLoadingLayer(active) {
      if (typeof document === 'undefined' || !document.body) return
      document.body.classList.toggle('position-preview-layer-open', Boolean(active))
    },
    reset() {
      this.primaryValue = this.category === 'hedge' && [2, 3].includes(Number(this.robotType)) ? 1 : 0
      this.futureType = 1
      this.coinSort = 2
      this.coinKeyword = ''
      this.rows = []
      this.error = false
    },
    close() {
      if (this.loading) this.requestId += 1
      this.$emit('update:visible', false)
    },
    selectPrimary(value) {
      if (this.primaryValue === value) return
      this.primaryValue = value
      this.coinKeyword = ''
      if (this.category === 'hedge') this.futureType = value === 1 ? 1 : ''
      this.load()
    },
    selectSettlement(value) {
      if (this.futureType === value) return
      this.futureType = value
      this.coinKeyword = ''
      this.load()
    },
    toggleSort() {
      this.coinSort = this.coinSort === 2 ? 1 : 2
      this.load()
    },
    requestOptions() {
      const options = {
        category: this.category,
        robotType: Number(this.robotType),
        coinSort: this.coinSort
      }
      if (this.category === 'term') options.version = [2, 3, 5][this.primaryValue] || 2
      if (this.category === 'vip') options.versionType = this.primaryValue === 1 ? 2 : 1
      if (this.category === 'hedge') {
        options.speed = this.primaryValue === 1 ? 2 : ''
        options.futureType = this.primaryValue === 1 ? this.futureType : ''
      }
      return options
    },
    async load() {
      if (
        this.robotType === null ||
        this.robotType === undefined ||
        !Number.isFinite(Number(this.robotType))
      ) return
      const requestId = ++this.requestId
      this.loading = true
      this.error = false
      try {
        const result = await loadStrategyPositionPreview(this.requestOptions())
        if (requestId !== this.requestId) return
        if (result.error) throw new Error(result.error)
        this.rows = result.rows
      } catch (error) {
        if (requestId !== this.requestId) return
        this.rows = []
        this.error = true
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    displayAmount(value) {
      if (value === null || value === undefined || value === '') return '--'
      return this.amountUnit ? `${value} ${this.amountUnit}` : String(value)
    }
  }
}
</script>

<style scoped>
.position-preview-modal {
  position: fixed;
  z-index: 15000;
  inset: 0;
  display: flex;
  padding: 24px;
  align-items: center;
  justify-content: center;
  background: rgba(20, 17, 38, .46);
  animation: position-preview-fade .18s ease;
}

.position-preview-modal__panel {
  display: flex;
  width: min(820px, calc(100vw - 48px));
  height: 750px;
  max-height: calc(100vh - 48px);
  padding: 26px;
  box-sizing: border-box;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20, 16, 45, .28);
  animation: position-preview-pop .2s ease;
}

.position-preview-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.position-preview-modal__header h2,
.position-preview-modal__header p { margin: 0; }
.position-preview-modal__header h2 { color: #151b2b; font-size: 20px; font-weight: 800; line-height: 28px; }
.position-preview-modal__header p { margin-top: 3px; color: #98a0af; font-size: 13px; }
.position-preview-modal__header > button { padding: 4px; color: #98a0af; border: 0; background: transparent; font-size: 17px; cursor: pointer; }

.position-preview-modal__tip {
  display: flex;
  margin: 0 0 16px;
  padding: 11px 13px;
  gap: 9px;
  color: #496baf;
  border-radius: 9px;
  background: #f0f5fe;
  font-size: 12.5px;
  line-height: 19px;
}
.position-preview-modal__tip i { margin-top: 3px; color: #1f6bff; }

.position-preview-tabs {
  display: inline-flex;
  margin-bottom: 15px;
  padding: 4px;
  gap: 3px;
  border-radius: 9px;
  background: #f2f4f8;
}
.position-preview-tabs--secondary { margin-left: 10px; }
.position-preview-tabs button {
  min-width: 86px;
  height: 34px;
  padding: 0 14px;
  color: #697386;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}
.position-preview-tabs button.active { color: #1f6bff; background: #fff; box-shadow: 0 1px 4px rgba(16, 24, 40, .08); font-weight: 700; }

.position-preview-filter {
  display: flex;
  width: 240px;
  height: 38px;
  margin: 0 0 14px auto;
  padding: 0 12px;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;
  color: #98a0af;
  border: 1px solid #dfe3eb;
  border-radius: 8px;
  background: #fff;
}
.position-preview-filter:focus-within { border-color: #1f6bff; box-shadow: 0 0 0 2px rgba(31, 107, 255, .08); }
.position-preview-filter input { width: 100%; min-width: 0; padding: 0; color: #303747; border: 0; outline: 0; background: transparent; font: 400 13px/1.4 inherit; }
.position-preview-filter input::placeholder { color: #b3bac8; font-weight: 400; }
.position-preview-filter button { display: inline-flex; padding: 0; flex: 0 0 auto; color: #a8b0bf; border: 0; background: transparent; cursor: pointer; }

.position-preview-table-wrap {
  min-height: 0;
  max-height: none;
  flex: 1;
  overflow: auto;
  border: 1px solid #e7eaf1;
  border-radius: 12px;
}
.position-preview-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.position-preview-table th,
.position-preview-table td { height: 48px; padding: 0 16px; text-align: center; border-bottom: 1px solid #edf0f5; }
.position-preview-table th { position: sticky; z-index: 1; top: 0; color: #79808f; background: #f7f8fa; font-size: 12px; font-weight: 600; }
.position-preview-table td { color: #303747; font-size: 13px; }
.position-preview-table tbody tr:last-child td { border-bottom: 0; }
.position-preview-table__sort { display: inline-flex; padding: 0; align-items: center; gap: 4px; color: inherit; border: 0; background: transparent; font: inherit; cursor: pointer; }

.position-preview-state { display: flex; min-height: 205px; align-items: center; justify-content: center; gap: 9px; color: #98a0af; font-size: 13px; }
.position-preview-state--error { flex-direction: column; }
.position-preview-state--error > i { color: #e5484d; font-size: 22px; }
.position-preview-state button { height: 34px; padding: 0 16px; color: #1f6bff; border: 1px solid #c9dafb; border-radius: 7px; background: #f4f7fe; cursor: pointer; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

@keyframes position-preview-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes position-preview-pop { from { opacity: 0; transform: translateY(8px) scale(.99); } to { opacity: 1; transform: none; } }

@media (max-width: 760px) {
  .position-preview-modal { align-items: flex-end; padding: 0; overflow: hidden; }
  .position-preview-modal__panel {
    position: relative;
    width: 100%;
    height: 683px;
    max-height: 100dvh;
    padding: 28px 16px calc(26px + env(safe-area-inset-bottom));
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -12px 36px rgba(16, 24, 40, .14);
    animation: position-preview-sheet-up .28s cubic-bezier(.22, .8, .3, 1) both;
  }
  .position-preview-modal__panel::before { content: ''; position: absolute; top: 8px; left: 50%; width: 36px; height: 4px; border-radius: 2px; background: #dce1e9; transform: translateX(-50%); }
  .position-preview-modal__header { margin-bottom: 14px; }
  .position-preview-modal__header h2 { font-size: 18px; line-height: 25px; }
  .position-preview-modal__tip { max-height: 70px; overflow-y: auto; }
  .position-preview-tabs { display: flex; width: 100%; margin: 0 0 10px; box-sizing: border-box; }
  .position-preview-tabs--secondary { margin-left: 0; }
  .position-preview-tabs button { min-width: 0; flex: 1; padding: 0 6px; font-size: 12px; }
  .position-preview-filter { width: 100%; height: 40px; margin-bottom: 12px; }
  .position-preview-table-wrap { min-height: 0; max-height: none; }
  .position-preview-table { min-width: 0; }
  .position-preview-table th,
  .position-preview-table td { height: 46px; padding: 0 11px; font-size: 12px; }
}

@keyframes position-preview-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
</style>

<style>
body.position-preview-layer-open > .el-loading-mask,
body.position-preview-layer-open .el-loading-mask.is-fullscreen {
  z-index: 15010 !important;
}
</style>